import { Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, lastValueFrom, Observable, Subscription, throwError } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { LoginService } from 'src/app/services/login.service';
import { PopUpDialogInformalService } from 'src/app/services/confirms/popUpDialogInformal.service';
import { User } from 'src/app/services/model/user.model';
import { CardButtonBackground, CardButtonSize } from 'src/app/components/ui-components/buttons/card-button/card-button.model';
import { PopUpInformalType } from 'src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model';
import { ClosingTransactionService, ClosingTransactionResponse, MoneyClosingData, ValuablesClosingData } from 'src/app/services/closings/closing-transaction.service';
import { SpinnerConfig } from 'src/app/functions/ui-utils';
import { PaymentTypesService } from 'src/app/services/payment-types.service';
import { SharedApiService } from 'src/app/services/api/sharedApi.service';
import { TenderType } from 'src/app/services/model/tender-type.enum';
import { SharedService } from 'src/app/services/shared.service';

export interface Denomination {
  value: number;
  count: number;
  total: number;
  registeredCount?: number;
}

interface LineNumberRef {
  current: number;
}

@Component({
  template: ''
})
export abstract class BaseClosingComponent implements OnDestroy {
  currentDate = new Date();
  closingForm!: FormGroup;
  user: User;
  currencyCode: string;
  denominations: Denomination[] = [];
  totalRegisteredValue = 0;
  totalValue = 0
  
  difference = 0;
  shortage = 0;
  surplus = 0;

  CardButtonSize = CardButtonSize;
  CardButtonBackground = CardButtonBackground;

  isSubmitting = false;
  protected formChangesSubscription?: Subscription;

  // Dependencies
  protected loginService = inject(LoginService);
  protected spinner = inject(NgxSpinnerService);
  protected popUpDialogService = inject(PopUpDialogInformalService);
  protected router = inject(Router);
  protected fb = inject(FormBuilder);
  protected closingTransactionService = inject(ClosingTransactionService);
  protected paymentTypesService = inject(PaymentTypesService);
  protected sharedApiService = inject(SharedApiService);
  protected _sharedService = inject(SharedService);

  constructor() {
    this.user = this.loginService.user;
    this.currencyCode = this.loginService.user?.CurrencyCode ?? 'CZK';

    // Initialize form with empty controls to prevent template errors
    this.closingForm = this.fb.group({
      accountingState: 0,
      confirm: false
    });
  }

  ngOnDestroy() {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }

  protected abstract calculateTotals(): void;

  protected subscribeToFormChanges() {
    this.formChangesSubscription = this.closingForm.valueChanges.subscribe(() => {
      this.calculateTotals();
    });
  }

  protected canSubmit(): boolean {
    if (!this.closingForm.get('confirm')?.value || this.isSubmitting) {
      return false;
    }
    this.isSubmitting = true;
    return true;
  }

  protected getControlValue(controlName: string): number {
    const control = this.closingForm.get(controlName);
    return control ? control.value || 0 : 0;
  }

  protected calculateDifference(total: number, registered: number) {
    this.difference = total - registered;

    if (this.difference < 0) {
      this.shortage = Math.abs(this.difference);
      this.surplus = 0;
    } else {
      this.shortage = 0;
      this.surplus = this.difference;
    }
  }

  protected handleSubmissionSuccess(spinnerName: string) {
    this.spinner.hide(spinnerName);
    this.isSubmitting = false;
  }

  protected handleSubmissionFailure(spinnerName: string, message: string | undefined, defaultMessageKey: string) {
    this.spinner.hide(spinnerName);
    this.isSubmitting = false;
    this.popUpDialogService.confirm({
      title: 'webPos.Errors.Error',
      message: message || defaultMessageKey,
      type: PopUpInformalType.Error
    });
  }

  protected handleSubmissionError(spinnerName: string, errorMessageKey: string, error?: any) {
    this.spinner.hide(spinnerName);
    this.isSubmitting = false;
    if (error) {
      console.error('Closing submission error:', error);
    }
    this.popUpDialogService.confirm({
      title: 'webPos.Errors.Error',
      message: errorMessageKey,
      type: PopUpInformalType.Error
    });
  }

  protected updateDenominations(denominations: Denomination[], controlPrefix: string) {
    let total = 0;
    let registeredTotal = 0;

    denominations.forEach((denomination, index) => {
      const count = this.getControlValue(`${controlPrefix}${index}`);
      denomination.count = count;
      denomination.total = denomination.value * count;
      total += denomination.total;
      registeredTotal += denomination.value * (denomination.registeredCount ?? 0);
    });

    return { total, registeredTotal };
  }

protected async confirm(isMoneyClosing: boolean) {
      if (!this.canSubmit()) {
        return null;
      }
  
      // Build denomination details array
      const denominationDetails: any[] = [];
       if (this.denominations.length > 0) {
      // Process denominations
      this.denominations.forEach((d, i) => {
        const count = this.closingForm.get(`denomination_${d.value}`).value || 0;
          denominationDetails.push({
            LineNumber: i+1,
            DenominationValue: d.value || 0,
            Count: count,
            RegisteredCount: d.registeredCount,
            Total: d.total || 0,
          });
      });
    } else {
      // Use summary value
      const summaryValue = this.closingForm.get('summaryValue')?.value || 0;
      denominationDetails.push({
        LineNumber: 1,
        DenominationValue: summaryValue,
        Count: 1,
        RegisteredCount: 0,
        Total: summaryValue,
      });
    }

    // assign tender type for valuables closing
    const paymentTypes = await this._sharedService.getPaymentTypes();
    const selectedPaymentName = this.closingForm.get('valuableType')?.value;
    const selectedPaymentType = paymentTypes.find(pt => pt.Name === selectedPaymentName);

    let closingTotalValue = this.totalValue;
    // We should keep track of registered value, not total, when creating money closing, as money closing is based on the total registered amount, not the total counted amount (which is the case of valuables closing)
    if (isMoneyClosing) {
      closingTotalValue = this.totalRegisteredValue;
    }

      // Prepare transaction data
    const transactionData: ValuablesClosingData = {
        // User information
        BusinessUnitID: this.user?.SelectedBusinessUnit,
        BusinessUnitName: this.user?.BusinessUnitName,
        DisplayName: this.user?.DisplayName,
        CurrencyCode: this.currencyCode,
        
        // Inventory information
        InventoryDate: this.currentDate,
        ValuableType: selectedPaymentName,
        TenderType: selectedPaymentType?.TenderType,
        TenderSubType: selectedPaymentType?.TenderSubType,
        TotalValue: closingTotalValue, 
        TotalRegisteredValue: this.totalRegisteredValue,
        Difference: this.difference,
        Surplus: this.surplus,
        Shortage: this.shortage,
        Note: this.closingForm.get('note')?.value || '',
        
        // Denomination details
        DenominationDetails: denominationDetails,
        
        // Options
        PrintReceipt: this.closingForm.get('print')?.value || false
      };
  
      // Show loading spinner
      this.spinner.show('valuablesClosing', SpinnerConfig.VALUABLES_CLOSING_SPINNER);
    
      let closingMethod: Observable<ClosingTransactionResponse>;
      if (isMoneyClosing) { 
        closingMethod = this.closingTransactionService.submitMoneyClosing(transactionData);
      } else {
        closingMethod = this.closingTransactionService.submitValuablesClosing(transactionData);
      }

      var res = await lastValueFrom(closingMethod.pipe(catchError(err => {
        this.handleSubmissionError('valuablesClosing', 'webPos.Closings.ClosingSubmissionError', err);
        return new Observable(observer => {
          observer.next(null);
          observer.complete();
        });
      }))) as ClosingTransactionResponse;

      if (res != null) {
            if (res.success) {
            this.handleSubmissionSuccess('valuablesClosing');
          } else {
            this.handleSubmissionFailure('valuablesClosing', res.message, 'webPos.Closings.ValuablesClosingFailed');
          }
      }
      return res;
    }
}
